/**
 * Error handling utilities
 * Provides centralized error handling and formatting
 */

import { logger } from '@/lib/monitoring/logger';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * True for the errors `handleError` rethrows rather than describes.
 *
 * Callers typically open their catch block with `handleError(...)`, so that
 * rethrow jumps straight past the code that tells the user anything. Ask this
 * first when the catch block has reporting to do, and the conditions stay in
 * one place: `handleError` is the only other reader of them.
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401 || error.status === 403;
  }

  if (error instanceof Error) {
    return (
      error.message.includes('unauthorized') ||
      error.message.includes('unauthenticated')
    );
  }

  return false;
}

export function handleError(error: unknown, context?: string): string {
  let message = 'An unexpected error occurred';

  if (error instanceof ApiError) {
    message = error.message;
    logger.error(`API Error in ${context}:`, error);
    // Only throw API errors that are critical (auth-related)
    if (error.status === 401 || error.status === 403) {
      throw error;
    }
  } else if (error instanceof Error) {
    message = error.message;
    logger.error(`Error in ${context}:`, error);
    // Only throw critical errors (auth-related)
    if (
      message.includes('unauthorized') ||
      message.includes('unauthenticated')
    ) {
      throw error;
    }
  }

  return message;
}

export function assertNonNullable<T>(
  value: T,
  message: string
): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error(message);
  }
}
